import styled from "styled-components";

import { colors, fonts } from "../../../styles/variables";
import breakpoints from "../../../styles/breakpoints";
import Button from "../../atoms/Button";

/* The shop banner: a bordered card under the wine pages' hero. Room on the
 * right keeps the copy and the Checkout action clear of the close X. */
export const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 44px 16px 20px;
  margin: 20px 0;
  background-color: ${colors.white};
  border: 1px solid ${colors.black};

  ${breakpoints.smDown} {
    flex-wrap: wrap;
    gap: 12px;
    padding: 12px 40px 12px 12px;
  }
`;

/* The close X, top right: removes the calendar (after a confirmation the
 * caller owns). The global reset leaves button colour to the UA, hence the
 * explicit transparent background. */
export const CloseX = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 32px;
  height: 32px;
  padding: 9px;
  border: 0;
  background: transparent;
  line-height: 0;
  cursor: pointer;

  img {
    display: block;
    width: 14px;
    height: 14px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
`;

export const Thumb = styled.img`
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  object-fit: contain;
  background-color: ${colors.merchCardDefaultBackgroundColor};

  ${breakpoints.smDown} {
    width: 48px;
    height: 48px;
  }
`;

export const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

export const Title = styled.p`
  margin: 0;
  font-family: ${fonts.fontBauMedium};
  font-size: 12px;
  line-height: 1.5;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const Headline = styled.p`
  margin: 2px 0 6px;
  font-family: ${fonts.fontInterstateBlackCompressed};
  font-size: 20px;
  line-height: 1.1;
  text-transform: none;
`;

export const Bar = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background-color: ${colors.silver};
  overflow: hidden;
`;

export const Fill = styled.div<{ $ratio: number }>`
  width: ${({ $ratio }) => `${Math.round(Math.min(Math.max($ratio, 0), 1) * 100)}%`};
  height: 100%;
  background-color: ${colors.success};
  transition: width 0.3s ease;
`;

/* "$215 of $275 of wine": spend progress for codes with a minimum total per box. */
export const SpendCount = styled.p`
  margin: 6px 0 0;
  font-family: ${fonts.fontBauMedium};
  font-size: 12px;
  line-height: 1.4;
  color: ${colors.gray};
`;

export const Detail = styled.p`
  margin: 6px 0 0;
  font-family: ${fonts.fontBauRegular};
  font-size: 13px;
  line-height: 1.4;
`;

/* The banner's standing note under the progress copy: the calendar is in the
 * one-off cart, and the rules that come with it. */
export const Note = styled.p`
  margin: 8px 0 0;
  font-family: ${fonts.fontBauRegular};
  font-size: 12px;
  line-height: 1.4;
  color: ${colors.gray};
`;

export const RemoveRow = styled.p`
  margin: 6px 0 0;
  font-family: ${fonts.fontBauRegular};
  font-size: 12px;
  line-height: 1.4;
  color: ${colors.gray};
`;

/* The global reset leaves button colour to the UA (blue on iOS), so set it. */
export const RemoveButton = styled.button`
  padding: 0;
  margin-left: 4px;
  border: 0;
  background: transparent;
  font-family: ${fonts.fontBauMedium};
  font-size: 12px;
  color: ${colors.black};
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
`;

/* The Checkout action: to the right of the copy, a full-width row of its own on phones. */
export const Actions = styled.div`
  flex-shrink: 0;
  align-self: center;

  ${breakpoints.smDown} {
    flex-basis: 100%;
  }
`;

/* Rendered as a router Link, so keep the Button's styling flags off the DOM. */
export const CheckoutLink = styled(Button).withConfig({
  shouldForwardProp: (prop) => !["full", "outline", "lg"].includes(String(prop)),
})`
  display: inline-block;
  text-decoration: none;

  ${breakpoints.smDown} {
    display: block;
    width: 100%;
  }
`;
